import * as React from "react";
import { channelDefs, dayOfWeekDefs, dayOfWeekKeys } from "@/types";
import {
  ChannelCondition,
  ChannelEditor
} from "./ConditionEditors/ChannelEditor";
import { DomainCondition, DomainEditor } from "./ConditionEditors/DomainEditor";
import {
  DayOfWeekCondition,
  DayOfWeekEditor
} from "./ConditionEditors/DayOfWeekEditor";
import {
  TimeOfDayCondition,
  TimeOfDayEditor
} from "./ConditionEditors/TimeOfDayEditor";
import { DateCondition, DateEditor } from "./ConditionEditors/DateEditor";
import { UrlCondition, UrlEditor } from "./ConditionEditors/UrlEditor";
import {
  AttributeCondition,
  AttributeEditor
} from "./ConditionEditors/AttributeEditor";
import { TeamCondition, TeamEditor } from "./ConditionEditors/TeamEditor";
import { comparatorDefs, ExpressionType, SerializedCondition } from "./types";
import {
  TimeOnPageCondition,
  TimeOnPageEditor
} from "./ConditionEditors/TimeOnPageEditor";
import {
  TeamAvailabilityCondition,
  TeamAvailabilityEditor
} from "./ConditionEditors/TeamAvailabilityEditor";
import {
  TeamAgentAvailabilityCondition,
  TeamAgentAvailabilityEditor
} from "./ConditionEditors/TeamAgentAvailabilityEditor";
import {
  TimePassedCondition,
  TimePassedEditor
} from "./ConditionEditors/TimePassedEditor";
import { TagCondition, TagEditor } from "./ConditionEditors/TagEditor";
import {
  CountryCondition,
  CountryEditor
} from "./ConditionEditors/CountryEditor";
import {
  LanguageCondition,
  LanguageEditor
} from "./ConditionEditors/LanguageEditor";
import {
  SharedScheduleCondition,
  SharedScheduleEditor
} from "./ConditionEditors/SharedScheduleEditor";
import {
  DeploymentCondition,
  DeploymentEditor
} from "./ConditionEditors/DeploymentEditor";
import {
  TeamQueueSizeCondition,
  TeamQueueSizeEditor
} from "./ConditionEditors/TeamQueueSizeEditor";
import {
  SelectOption,
  teamAvailabilityStatusDefs
} from "@velaro/velaro-component-lib";

interface ConditionProps {
  condition: SerializedCondition;
  onUpdate: (condition: SerializedCondition) => void;
  onRemove?: () => void;
}

interface ConditionDef {
  label: string;
  editor: (props: ConditionProps) => JSX.Element;
  toText: (
    condition: SerializedCondition,
    getTeamNameById: (id: number) => string
  ) => string;
  whitelist?: ExpressionType[]; //if this has a value, only these expression types will have this condition
  blacklist?: ExpressionType[]; //if this has a value, these expression types will not have this condition
}

export const conditionDefs: Record<string, ConditionDef> = {
  channel: {
    label: "Channel",
    editor: ChannelEditor,
    toText: (condition: SerializedCondition) => {
      if (!condition || !condition.data || !condition.type) {
        return "--condition is incomplete--";
      }
      const channelCondition = JSON.parse(condition.data) as ChannelCondition;
      if (!channelCondition.comparator || !channelCondition.channel) {
        return "--condition is incomplete--";
      }
      return `channel ${comparatorDefs[channelCondition.comparator]} ${
        channelDefs[channelCondition.channel]
      }`;
    },
    blacklist: [
      // "determine-language-config", // Commented out - outdated
      // "determine-style-config", // Commented out - outdated
      "send-invite"
    ]
  },
  timeOnPage: {
    label: "Time On Page",
    editor: TimeOnPageEditor,
    toText: (condition: SerializedCondition) => {
      if (!condition || !condition.data || !condition.type) {
        return "--condition is incomplete--";
      }
      const timeOnPage = JSON.parse(condition.data) as TimeOnPageCondition;
      return `time on page is ${timeOnPage.seconds} seconds`;
    },
    whitelist: ["send-invite"]
  },
  scrollDepth: {
    label: "Scroll Depth",
    editor: ({ condition, onUpdate }: ConditionProps) => {
      const data = JSON.parse(condition.data || '{"percent":50}');
      return (
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-600">visitor scrolled at least</span>
          <input
            type="number"
            min={1}
            max={100}
            value={data.percent}
            onChange={e => onUpdate({ ...condition, data: JSON.stringify({ percent: parseInt(e.target.value) || 50 }) })}
            className="w-20 border rounded px-2 py-1 text-sm"
          />
          <span className="text-sm text-slate-600">% of the page</span>
        </div>
      );
    },
    toText: (condition: SerializedCondition) => {
      const d = JSON.parse(condition.data || '{"percent":50}');
      return `visitor scrolled ≥ ${d.percent}% of the page`;
    },
    whitelist: ["send-invite"] as ExpressionType[]
  },
  pageHits: {
    label: "Page Views",
    editor: ({ condition, onUpdate }: ConditionProps) => {
      const data = JSON.parse(condition.data || '{"minPages":2}');
      return (
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-600">visitor has viewed at least</span>
          <input
            type="number"
            min={1}
            value={data.minPages}
            onChange={e => onUpdate({ ...condition, data: JSON.stringify({ minPages: parseInt(e.target.value) || 2 }) })}
            className="w-16 border rounded px-2 py-1 text-sm"
          />
          <span className="text-sm text-slate-600">pages this session</span>
        </div>
      );
    },
    toText: (condition: SerializedCondition) => {
      const d = JSON.parse(condition.data || '{"minPages":2}');
      return `visitor viewed ≥ ${d.minPages} pages`;
    },
    whitelist: ["send-invite"] as ExpressionType[]
  },
  exitIntent: {
    label: "Exit Intent",
    editor: () => (
      <span className="text-sm text-slate-600">visitor&apos;s cursor moves toward the browser chrome (about to leave)</span>
    ),
    toText: () => "visitor shows exit intent",
    whitelist: ["send-invite"] as ExpressionType[]
  },
  referrer: {
    label: "Referrer",
    editor: ({ condition, onUpdate }: ConditionProps) => {
      const data = JSON.parse(condition.data || '{"comparator":"contains","value":""}');
      return (
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-600">referrer</span>
          <select
            value={data.comparator}
            onChange={e => onUpdate({ ...condition, data: JSON.stringify({ ...data, comparator: e.target.value }) })}
            className="border rounded px-2 py-1 text-sm"
          >
            <option value="contains">contains</option>
            <option value="notContains">doesn&apos;t contain</option>
            <option value="equals">equals</option>
            <option value="regex">matches pattern</option>
          </select>
          <input
            type="text"
            value={data.value}
            placeholder="e.g. google.com"
            onChange={e => onUpdate({ ...condition, data: JSON.stringify({ ...data, value: e.target.value }) })}
            className="border rounded px-2 py-1 text-sm w-48"
          />
        </div>
      );
    },
    toText: (condition: SerializedCondition) => {
      const d = JSON.parse(condition.data || '{"comparator":"contains","value":""}');
      return `referrer ${d.comparator} "${d.value}"`;
    },
    whitelist: ["send-invite"] as ExpressionType[]
  },
  utmCampaign: {
    label: "UTM Campaign",
    editor: ({ condition, onUpdate }: ConditionProps) => {
      const data = JSON.parse(condition.data || '{"comparator":"equals","value":""}');
      return (
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-600">utm_campaign</span>
          <select
            value={data.comparator}
            onChange={e => onUpdate({ ...condition, data: JSON.stringify({ ...data, comparator: e.target.value }) })}
            className="border rounded px-2 py-1 text-sm"
          >
            <option value="equals">equals</option>
            <option value="contains">contains</option>
            <option value="regex">matches pattern</option>
          </select>
          <input
            type="text"
            value={data.value}
            placeholder="e.g. summer-sale"
            onChange={e => onUpdate({ ...condition, data: JSON.stringify({ ...data, value: e.target.value }) })}
            className="border rounded px-2 py-1 text-sm w-48"
          />
        </div>
      );
    },
    toText: (condition: SerializedCondition) => {
      const d = JSON.parse(condition.data || '{"comparator":"equals","value":""}');
      return `utm_campaign ${d.comparator} "${d.value}"`;
    },
    whitelist: ["send-invite"] as ExpressionType[]
  },
  returnVisitor: {
    label: "Return Visitor",
    editor: ({ condition, onUpdate }: ConditionProps) => {
      const data = JSON.parse(condition.data || '{"isReturn":true}');
      return (
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-600">visitor is a</span>
          <select
            value={data.isReturn ? "return" : "first"}
            onChange={e => onUpdate({ ...condition, data: JSON.stringify({ isReturn: e.target.value === "return" }) })}
            className="border rounded px-2 py-1 text-sm"
          >
            <option value="return">returning visitor</option>
            <option value="first">first-time visitor</option>
          </select>
        </div>
      );
    },
    toText: (condition: SerializedCondition) => {
      const d = JSON.parse(condition.data || '{"isReturn":true}');
      return d.isReturn ? "visitor is returning" : "visitor is first-time";
    },
    whitelist: ["send-invite"] as ExpressionType[]
  },
  domain: {
    label: "Domain",
    editor: DomainEditor,
    toText: (condition: SerializedCondition) => {
      if (!condition || !condition.data || !condition.type) {
        return "--condition is incomplete--";
      }
      const domainCondition = JSON.parse(condition.data) as DomainCondition;
      return `domain ${comparatorDefs[domainCondition.comparator]} ${
        domainCondition.domain
      }`;
    }
  },
  url: {
    label: "Url",
    editor: UrlEditor,
    toText: (condition: SerializedCondition) => {
      if (!condition || !condition.data || !condition.type) {
        return "--condition is incomplete--";
      }
      const urlCondition = JSON.parse(condition.data) as UrlCondition;

      return `url ${comparatorDefs[urlCondition.comparator]} ${
        urlCondition.url
      }`;
    }
  },
  dayOfWeek: {
    label: "Day of Week",
    editor: DayOfWeekEditor,
    toText: (condition: SerializedCondition) => {
      if (!condition || !condition.data || !condition.type) {
        return "--condition is incomplete--";
      }
      const dayOfWeekCondition = JSON.parse(
        condition.data
      ) as DayOfWeekCondition;

      let daysText = "";
      const daysOfWeekSorted = dayOfWeekCondition.daysOfWeek.sort(
        (a, b) => dayOfWeekKeys.indexOf(a) - dayOfWeekKeys.indexOf(b)
      );
      const count = daysOfWeekSorted.length;

      if (count > 1) {
        daysOfWeekSorted.forEach((day, i) => {
          const dayName = dayOfWeekDefs[day];

          const last = i === daysOfWeekSorted.length - 1;

          if (last) {
            daysText += `or ${dayName}`;
          } else {
            daysText += `${dayName}`;
            if (count > 2) {
              daysText += ", ";
            } else {
              daysText += " ";
            }
          }
        });
      } else {
        daysText = dayOfWeekDefs[daysOfWeekSorted[0]];
      }

      return `day of week is ${daysText}`;
    }
  },
  timeOfDay: {
    label: "Time of Day",
    editor: TimeOfDayEditor,
    toText: (condition: SerializedCondition) => {
      if (!condition || !condition.data || !condition.type) {
        return "--condition is incomplete--";
      }
      const timeOfDayCondition = JSON.parse(
        condition.data
      ) as TimeOfDayCondition;
      return `time of day is ${timeOfDayCondition.from} to ${timeOfDayCondition.to}`;
    }
  },
  date: {
    label: "Date",
    editor: DateEditor,
    toText: (condition: SerializedCondition) => {
      if (!condition || !condition.data || !condition.type) {
        return "--condition is incomplete--";
      }
      const dateCondition = JSON.parse(condition.data) as DateCondition;
      const recurringText = dateCondition.recurring ? " (recurring yearly)" : "";
      return `date ${comparatorDefs[dateCondition.comparator]} ${
        dateCondition.date
      }${recurringText}`;
    }
  },
  attribute: {
    label: "Attribute",
    editor: AttributeEditor,
    toText: (condition: SerializedCondition) => {
      if (!condition || !condition.data || !condition.type) {
        return "--condition is incomplete--";
      }
      const attributeCondition = JSON.parse(
        condition.data
      ) as AttributeCondition;

      return `${attributeCondition.attribute} ${
        comparatorDefs[attributeCondition.comparator]
      } ${attributeCondition.value}`;
    }
  },
  team: {
    label: "Team",
    editor: TeamEditor,
    toText: (condition: SerializedCondition, getTeamNameById) => {
      if (!condition || !condition.data || !condition.type) {
        return "--condition is incomplete--";
      }
      const teamCondition = JSON.parse(condition.data) as TeamCondition;

      let teamsText = "";
      if (teamCondition.teamIds.length > 1) {
        teamCondition.teamIds.forEach((teamId, i) => {
          const teamName = getTeamNameById(teamId);
          if (i === teamCondition.teamIds.length - 1) {
            teamsText += `or ${teamName}`;
          } else {
            teamsText += `${teamName}`;
            if (teamCondition.teamIds.length > 2) {
              teamsText += ", ";
            } else {
              teamsText += " ";
            }
          }
        });
      } else {
        teamsText = getTeamNameById(teamCondition.teamIds[0]);
      }

      return `team ${comparatorDefs[teamCondition.comparator]} ${teamsText}`;
    }
  },
  teamAvailability: {
    label: "Team Availability",
    editor: TeamAvailabilityEditor,
    toText: (condition: SerializedCondition) => {
      const teamAvailabilityCondition = JSON.parse(
        condition.data
      ) as TeamAvailabilityCondition;
      return `team ${comparatorDefs[teamAvailabilityCondition.comparator]} ${
        teamAvailabilityStatusDefs[teamAvailabilityCondition.status]
      }`;
    }
  },
  teamAgentAvailability: {
    label: "Agent Available in Team",
    editor: TeamAgentAvailabilityEditor,
    toText: (condition: SerializedCondition, getTeamNameById) => {
      if (!condition || !condition.data) return "--condition is incomplete--";
      const c = JSON.parse(
        condition.data
      ) as TeamAgentAvailabilityCondition;
      if (!c.teamId) return "--no team selected--";
      const teamName = getTeamNameById(c.teamId) ?? `team ${c.teamId}`;
      const comparator = c.comparator === "notEquals" ? "is not" : "is";
      const statusLabel =
        c.status === "Unavailable"
          ? "offline"
          : c.status === "Busy"
            ? "busy"
            : "online";
      return `${teamName} ${comparator} ${statusLabel}`;
    }
  },
  timePassed: {
    label: "Time Passed",
    editor: TimePassedEditor,
    toText: (condition: SerializedCondition) => {
      if (!condition || !condition.data || !condition.type) {
        return "--condition is incomplete--";
      }
      const timePassedCondition = JSON.parse(
        condition.data
      ) as TimePassedCondition;
      const parts = [];
      if (timePassedCondition.days > 0) {
        parts.push(`${timePassedCondition.days} days`);
      }
      if (timePassedCondition.hours > 0) {
        parts.push(`${timePassedCondition.hours} hours`);
      }
      if (timePassedCondition.minutes > 0) {
        parts.push(`${timePassedCondition.minutes} minutes`);
      }
      return `time passed is ${parts.join(", ") || "0 minutes"}`;
    }
  },
  tag: {
    label: "Tag",
    editor: TagEditor,
    toText: (condition: SerializedCondition) => {
      if (!condition || !condition.data || !condition.type) {
        return "--condition is incomplete--";
      }
      const tagCondition = JSON.parse(condition.data) as TagCondition;
      const tagTypeLabel =
        tagCondition.tagType === "conversation" ? "conversation" : "contact";
      const hasTagLabel = tagCondition.hasTag ? "has tag" : "does not have tag";
      return `${tagTypeLabel} ${hasTagLabel} #${tagCondition.tagId}`;
    }
  },
  country: {
    label: "Country",
    editor: CountryEditor,
    toText: (condition: SerializedCondition) => {
      if (!condition || !condition.data || !condition.type) {
        return "--condition is incomplete--";
      }
      const countryCondition = JSON.parse(condition.data) as CountryCondition;
      if (!countryCondition.comparator) {
        return "--condition is incomplete--";
      }
      return `country ${comparatorDefs[countryCondition.comparator]} ${
        countryCondition.country || ""
      }`;
    },
    blacklist: [
      // "determine-language-config", // Commented out - outdated
      // "determine-style-config", // Commented out - outdated
      "send-invite"
    ]
  },
  language: {
    label: "Language",
    editor: LanguageEditor,
    toText: (condition: SerializedCondition) => {
      if (!condition || !condition.data || !condition.type) {
        return "--condition is incomplete--";
      }
      const languageCondition = JSON.parse(condition.data) as LanguageCondition;
      if (!languageCondition.comparator) {
        return "--condition is incomplete--";
      }
      return `language ${comparatorDefs[languageCondition.comparator]} ${
        languageCondition.language || ""
      }`;
    },
    blacklist: [
      // "determine-language-config", // Commented out - outdated
      // "determine-style-config", // Commented out - outdated
      "send-invite"
    ]
  },
  sharedSchedule: {
    label: "Named Schedule",
    editor: SharedScheduleEditor,
    toText: (condition: SerializedCondition) => {
      if (!condition || !condition.data || !condition.type) {
        return "--condition is incomplete--";
      }
      const c = JSON.parse(condition.data) as SharedScheduleCondition;
      return `within named schedule #${c.scheduleId ?? "?"}`;
    }
  },
  deployment: {
    label: "Chat Button",
    editor: DeploymentEditor,
    toText: (condition: SerializedCondition) => {
      if (!condition || !condition.data || !condition.type) {
        return "--condition is incomplete--";
      }
      const c = JSON.parse(condition.data) as DeploymentCondition;
      const ids = c.deploymentIds ?? [];
      if (ids.length === 0) return "chat button is (none selected)";
      if (ids.length === 1) return `chat button is ${ids[0]}`;
      return `chat button is one of ${ids.join(", ")}`;
    }
  },
  teamQueueSize: {
    label: "Team Queue Depth",
    editor: TeamQueueSizeEditor,
    toText: (condition: SerializedCondition, getTeamNameById) => {
      if (!condition || !condition.data) return "--condition is incomplete--";
      const c = JSON.parse(condition.data) as TeamQueueSizeCondition;
      if (!c.teamId) return "--no team selected--";
      const teamName = getTeamNameById(c.teamId) ?? `team ${c.teamId}`;
      const comparatorLabel: Record<string, string> = {
        greaterThan: "more than",
        greaterThanOrEqual: "at least",
        lessThan: "fewer than",
        equals: "exactly"
      };
      const cmp = comparatorLabel[c.comparator] ?? "more than";
      return `${teamName} queue has ${cmp} ${c.threshold ?? 5} waiting`;
    }
  }
};

export type Condition = keyof typeof conditionDefs;
export const conditionOptions: SelectOption[] = Object.keys(conditionDefs).map(
  (key: Condition) => {
    return {
      label: conditionDefs[key].label,
      value: key
    };
  }
);
